import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Area, Bar, BarChart, CartesianGrid, ComposedChart, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { BatteryCharging, ChevronLeft, ChevronRight, RefreshCw, ShieldCheck } from 'lucide-react';

import { loadMobileDeviceHealth } from './api';
import { Empty, Failed, Loading } from './parts';
import { tx } from './i18n';
import { useLiveResource } from './useLive';
import {
  BatteryMark, CHART, COMPACT, ChartTip, NUMBER, RangeTabs, ago, averageOf, counted, lengthOf, originLabel, percent,
  stackRows, tipClock, tipDay, useNow, versusAverage,
} from './MobileParts';
import { DeviceArt, deviceLook, finishLabel } from './DeviceArt';
import { useAppStrings } from './AppPhone';

// Navy, lightest to darkest, and one warm tone for time awake in the night.
const INK = '#203a85';
const MID = '#7185bd';
const SOFT = '#a9b5d9';
const PALE = '#e6e9f2';
const TICK = { fill: '#8b93ad', fontSize: 11 };
const GRID = '#eef1f7';
const STAGES = [['deep', '#0a1e57'], ['rem', '#435ba1'], ['light', '#a9b5d9'], ['awake', '#e8b9a6']];

const HOUR = 3600000;
const DECIMAL = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 });
const KILOMETRES = new Intl.NumberFormat(undefined, { style: 'unit', unit: 'kilometer', maximumFractionDigits: 1 });
const MILLISECONDS = new Intl.NumberFormat(undefined, { style: 'unit', unit: 'millisecond', maximumFractionDigits: 0 });
const PERCENT = new Intl.NumberFormat(undefined, { style: 'percent', maximumFractionDigits: 0 });

function dateOn(at, zone) {
  try {
    return new Date(at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: zone });
  } catch {
    return new Date(at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
}

function present(days, field = 'value') {
  return days.filter((entry) => entry[field] !== null && entry[field] !== undefined);
}

function mean(values) {
  return values.length === 0 ? null : values.reduce((sum, value) => sum + value, 0) / values.length;
}

const FORMAT = {
  heartRate: (value) => tx('ui.mobile.value_bpm', { value: NUMBER.format(Math.round(value)) }),
  hrv: (value) => MILLISECONDS.format(value),
  bloodOxygen: (value) => PERCENT.format(value / 100),
  respiratoryRate: (value) => tx('ui.mobile.value_breaths', { value: DECIMAL.format(value) }),
  bodyTemperature: (value) => tx('ui.mobile.value_celsius', { value: DECIMAL.format(value) }),
  steps: (value) => counted('ui.mobile.value_steps', Math.round(value)),
  distance: (value) => KILOMETRES.format(value / 1000),
  calories: (value) => tx('ui.mobile.value_kcal', { value: NUMBER.format(Math.round(value)) }),
};

function Card({ title, value, detail, wide = false, empty = false, children }) {
  return (
    <section className={`xo-mob-health-card${wide ? ' is-wide' : ''}`}>
      <header>
        <span>{title}</span>
        {!empty && <strong>{value}</strong>}
        {!empty && detail && <small>{detail}</small>}
      </header>
      {empty ? <p className="xo-mob-health-empty">{tx('ui.mobile.no_readings')}</p> : <div className="xo-mob-health-chart">{children}</div>}
    </section>
  );
}

// A day's hover card: the metric's value, and the range's average beside it.
function dayFacts(value, average, format) {
  if (value === null || value === undefined) return [];
  return [
    average === null ? null : { label: tx('ui.mobile.tip_range_average'), value: format(average) },
    versusAverage(value, average),
  ];
}

// A chart's points are days, or hours across the last twenty-four (`when` names them).
function DailyBars({ days, when, format, name, color = INK }) {
  const average = averageOf(days.map((entry) => entry.value));
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={days} margin={{ top: 6, right: 4, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke={GRID} />
        <XAxis dataKey="at" tickFormatter={when.tick} tickLine={false} axisLine={false} minTickGap={24} tick={TICK} />
        <YAxis tickLine={false} axisLine={false} tick={TICK} tickFormatter={(value) => COMPACT.format(value)} width={44} />
        <Tooltip cursor={CHART.cursor} animationDuration={120} filterNull={false} content={(
          <ChartTip title={when.title}
            rows={(point) => [{ name, color, text: point.value === null ? null : format(point.value) }]}
            facts={(point) => (point.value === null ? [{ note: tx('ui.mobile.tip_no_reading') }] : dayFacts(point.value, average, format))} />
        )} />
        <Bar dataKey="value" fill={color} radius={[3, 3, 0, 0]} maxBarSize={18} name={name} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function DailyLine({ days, when, format, name, secondName = null }) {
  const average = averageOf(days.map((entry) => entry.value));
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={days} margin={{ top: 6, right: 4, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke={GRID} />
        <XAxis dataKey="at" tickFormatter={when.tick} tickLine={false} axisLine={false} minTickGap={24} tick={TICK} />
        <YAxis tickLine={false} axisLine={false} tick={TICK} domain={['auto', 'auto']} width={44} tickFormatter={(value) => DECIMAL.format(value)} />
        <Tooltip cursor={CHART.lineCursor} animationDuration={120} filterNull={false} content={(
          <ChartTip title={when.title}
            rows={(point) => [
              { name, color: INK, mark: 'line', text: point.value === null || point.value === undefined ? null : format(point.value) },
              secondName ? { name: secondName, color: MID, mark: 'line', text: point.second === null || point.second === undefined ? null : format(point.second) } : null,
            ]}
            facts={(point) => (point.value === null || point.value === undefined
              ? [{ note: tx('ui.mobile.tip_no_reading') }] : dayFacts(point.value, average, format))} />
        )} />
        <Line type="monotone" dataKey="value" stroke={INK} strokeWidth={2} dot={{ r: 2.5, fill: INK }} connectNulls={false} name={name} />
        {secondName && <Line type="monotone" dataKey="second" stroke={MID} strokeWidth={2} dot={{ r: 2.5, fill: MID }} connectNulls={false} name={secondName} />}
      </LineChart>
    </ResponsiveContainer>
  );
}

// Each day's (or hour's) lowest to highest reading as a band, its average as a line.
function DailyRange({ days, when, format, averageName }) {
  const data = days.map((entry) => ({ ...entry, band: entry.value === null ? null : [entry.lo, entry.hi] }));
  const average = averageOf(days.map((entry) => entry.value));
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} margin={{ top: 6, right: 4, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke={GRID} />
        <XAxis dataKey="at" tickFormatter={when.tick} tickLine={false} axisLine={false} minTickGap={24} tick={TICK} />
        <YAxis tickLine={false} axisLine={false} tick={TICK} domain={['auto', 'auto']} width={44} />
        <Tooltip cursor={CHART.lineCursor} animationDuration={120} filterNull={false} content={(
          <ChartTip title={when.title}
            rows={(point) => (point.value === null ? [] : [
              { name: averageName, color: INK, mark: 'line', text: format(point.value) },
              { name: tx('ui.mobile.tip_highest'), color: SOFT, mark: 'band', text: point.hi === null ? null : format(point.hi) },
              { name: tx('ui.mobile.tip_lowest'), color: SOFT, mark: 'band', text: point.lo === null ? null : format(point.lo) },
            ])}
            facts={(point) => (point.value === null ? [{ note: tx('ui.mobile.tip_no_reading') }] : [
              point.hi !== null && point.lo !== null ? { label: tx('ui.mobile.tip_spread'), value: format(point.hi - point.lo) } : null,
              ...dayFacts(point.value, average, format),
            ])} />
        )} />
        <Area type="monotone" dataKey="band" stroke="none" fill={SOFT} fillOpacity={0.45} connectNulls={false} name={tx('ui.mobile.daily_range')} />
        <Line type="monotone" dataKey="value" stroke={INK} strokeWidth={2} dot={{ r: 2.5, fill: INK }} connectNulls={false} name={averageName} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function SleepNights({ nights, zone }) {
  const data = nights.map((night) => ({ at: night.at, start: night.start, end: night.end,
    ...Object.fromEntries(STAGES.map(([stage]) => [stage, night.stages[stage]])), minutes: night.minutes }));
  const average = averageOf(nights.map((night) => night.minutes));
  const stages = STAGES.map(([stage, color]) => ({ key: stage, name: tx(`ui.mobile.stage_${stage}`), color }));
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 6, right: 4, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke={GRID} />
        <XAxis dataKey="at" tickFormatter={(at) => dateOn(at, zone)} tickLine={false} axisLine={false} minTickGap={24} tick={TICK} />
        <YAxis tickLine={false} axisLine={false} tick={TICK} width={44} tickFormatter={(minutes) => DECIMAL.format(minutes / 60)} />
        <Tooltip cursor={CHART.cursor} animationDuration={120} content={(
          <ChartTip
            title={(at, point) => ({
              main: tx('ui.mobile.tip_night_of', { day: tipDay(at, zone).main }),
              sub: point.start && point.end ? tx('ui.mobile.tip_asleep_span', { from: tipClock(point.start, zone), to: tipClock(point.end, zone) }) : null,
            })}
            rows={(point) => stackRows(point, stages, { format: (minutes) => lengthOf(minutes * 60000) })}
            facts={(point) => [
              { label: tx('ui.mobile.tip_night_total'), value: lengthOf(point.minutes * 60000) },
              versusAverage(point.minutes, average),
            ]} />
        )} />
        {STAGES.map(([stage, color], index) => (
          <Bar key={stage} dataKey={stage} stackId="night" fill={color} maxBarSize={22} name={tx(`ui.mobile.stage_${stage}`)}
            radius={index === STAGES.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function LatestDay({ day, zone }) {
  const daySteps = day.hours.reduce((sum, slot) => sum + (slot.steps || 0), 0);
  const dayBeats = averageOf(day.hours.map((slot) => slot.heartRate));
  const hourName = (hour) => `${String(hour).padStart(2, '0')}:00`;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={day.hours} margin={{ top: 6, right: 0, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke={GRID} />
        <XAxis dataKey="hour" tickLine={false} axisLine={false} tick={TICK} interval={2} tickFormatter={(hour) => String(hour).padStart(2, '0')} />
        <YAxis yAxisId="beats" tickLine={false} axisLine={false} tick={TICK} domain={['auto', 'auto']} width={44} />
        <YAxis yAxisId="steps" orientation="right" hide />
        <Tooltip cursor={CHART.cursor} animationDuration={120} filterNull={false} content={(
          <ChartTip
            title={(hour) => ({ main: tx('ui.mobile.tip_hour_span', { from: hourName(hour), to: hourName((hour + 1) % 24) }), sub: tipDay(day.at, zone).main })}
            rows={(point) => [
              { name: tx('ui.mobile.metric_heart_rate'), color: INK, mark: 'line', text: point.heartRate === null ? null : FORMAT.heartRate(point.heartRate) },
              { name: tx('ui.mobile.metric_steps'), color: PALE, mark: 'bar', text: point.steps === null ? null : FORMAT.steps(point.steps) },
            ]}
            facts={(point) => [
              point.steps !== null && daySteps > 0 ? { label: tx('ui.mobile.tip_day_steps_share'), value: percent(point.steps, daySteps) } : null,
              point.heartRate !== null ? { ...versusAverage(point.heartRate, dayBeats), label: tx('ui.mobile.tip_vs_day_heart_rate') } : null,
              point.heartRate === null && point.steps === null ? { note: tx('ui.mobile.tip_no_reading_hour') } : null,
            ]} />
        )} />
        <Bar yAxisId="steps" dataKey="steps" fill={PALE} radius={[3, 3, 0, 0]} maxBarSize={14} name={tx('ui.mobile.metric_steps')} />
        <Line yAxisId="beats" type="monotone" dataKey="heartRate" stroke={INK} strokeWidth={2} dot={false} connectNulls={false} name={tx('ui.mobile.metric_heart_rate')} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function summary(days) {
  const values = present(days).map((entry) => entry.value);
  if (values.length === 0) return null;
  return { average: mean(values), total: values.reduce((sum, value) => sum + value, 0), latest: values[values.length - 1], days: values.length };
}

// Everything a ring or band recorded in the range, drawn from its own readings.
export default function DeviceHealth({ deviceId, backTo = null, onBack = null }) {
  const [days, setDays] = useState(30);
  const load = useCallback(() => loadMobileDeviceHealth(deviceId, days), [deviceId, days]);
  const report = useLiveResource([], load, { reconcileMs: 120000 });
  const now = useNow(30000);
  const as = useAppStrings();
  const data = report.data;

  const cards = useMemo(() => {
    if (!data) return null;
    const metric = (key) => data.metrics[key] ? data.metrics[key].days : [];
    return {
      heartRate: summary(metric('heartRate')),
      steps: summary(metric('steps')),
      hrv: summary(metric('hrv')),
      bloodOxygen: summary(metric('bloodOxygen')),
      respiratoryRate: summary(metric('respiratoryRate')),
      calories: summary(metric('calories')),
      distance: summary(metric('distance')),
      bloodPressure: (() => {
        const values = present(metric('bloodPressure'));
        return values.length === 0 ? null : { systolic: mean(values.map((entry) => entry.value)), diastolic: mean(values.map((entry) => entry.second)) };
      })(),
      sleep: data.sleep.nights.length === 0 ? null : { average: mean(data.sleep.nights.map((night) => night.minutes)), nights: data.sleep.nights.length },
    };
  }, [data]);

  if (report.loading && data === null) return <Loading label={tx('ui.mobile.reading_health')} />;
  if (report.error && data === null) return <Failed message={report.error} onRetry={report.refresh} />;
  const { device, account, zone } = data;
  const metric = (key) => (data.metrics[key] ? data.metrics[key].days : []);
  const finish = finishLabel(deviceLook(device.origin, device.name), as);
  // The last twenty-four hours come hour by hour; a longer range day by day.
  const hourly = data.step === HOUR;
  const when = hourly
    ? {
      tick: (at) => tipClock(at, zone),
      title: (at) => ({ main: tx('ui.mobile.tip_hour_span', { from: tipClock(at, zone), to: tipClock(at + HOUR, zone) }), sub: tipDay(at, zone).main }),
    }
    : { tick: (at) => dateOn(at, zone), title: (at) => tipDay(at, zone) };
  // A count adds up over the last day, and averages per day over longer.
  const counts = (card) => (hourly ? card.total : card.average);
  const countDetail = tx(hourly ? 'ui.mobile.in_last_24h' : 'ui.mobile.per_day_average');
  const meanDetail = tx(hourly ? 'ui.mobile.average_last_24h' : 'ui.mobile.average_in_range');
  const back = tx('ui.mobile.back_to_devices');

  return (
    <div className="xo-mob-health">
      <header className="xo-mob-health-head">
        <div className="xo-mob-health-id">
          {(backTo || onBack) && (
            backTo
              ? <Link className="xo-mob-back-chevron" to={backTo} aria-label={back} title={back}><ChevronLeft aria-hidden="true" /></Link>
              : <button type="button" className="xo-mob-back-chevron" onClick={onBack} aria-label={back} title={back}><ChevronLeft aria-hidden="true" /></button>
          )}
          <DeviceArt origin={device.origin} name={device.name} size={76} className="is-tile" title={originLabel(device.origin)} />
          <div>
            <h2>{originLabel(device.origin)}{device.macTail && <small>…{device.macTail}</small>}</h2>
            {finish && <p className="xo-mob-finish">{finish}</p>}
            <p>
              {account.accountUuid
                ? <Link to={`/xomics/mobile/accounts?account=${account.accountUuid}`}>{account.name || account.email}<ChevronRight aria-hidden="true" /></Link>
                : <span>{account.name || account.email}</span>}
            </p>
            <ul className="xo-mob-health-facts">
              <li><RefreshCw aria-hidden="true" />{device.lastSuccessAt ? tx('ui.mobile.synced_ago', { when: ago(device.lastSuccessAt, now) }) : tx('ui.mobile.not_synced_yet')}</li>
              <li>{tx('ui.mobile.paired_on', { date: new Date(device.pairedAt).toLocaleDateString(undefined, { dateStyle: 'medium' }) })}</li>
              {device.battery !== null && <li><BatteryCharging aria-hidden="true" /><BatteryMark value={device.battery} charging={device.charging} /></li>}
            </ul>
          </div>
        </div>
        <RangeTabs value={days} onChange={setDays} />
      </header>

      <div className="xo-mob-health-grid">
        <Card wide title={tx('ui.mobile.metric_sleep')} empty={cards.sleep === null}
          value={cards.sleep && lengthOf(cards.sleep.average * 60000)} detail={cards.sleep && counted('ui.mobile.nights_average', cards.sleep.nights)}>
          <SleepNights nights={data.sleep.nights} zone={zone} />
          <div className="xo-mob-key">{STAGES.map(([stage, color]) => <span key={stage}><i style={{ background: color }} />{tx(`ui.mobile.stage_${stage}`)}</span>)}</div>
        </Card>
        <Card wide title={tx('ui.mobile.metric_heart_rate')} empty={cards.heartRate === null}
          value={cards.heartRate && FORMAT.heartRate(cards.heartRate.average)} detail={tx(hourly ? 'ui.mobile.hourly_range_detail' : 'ui.mobile.daily_range_detail')}>
          <DailyRange days={metric('heartRate')} when={when} format={FORMAT.heartRate}
            averageName={tx(hourly ? 'ui.mobile.metric_heart_rate' : 'ui.mobile.daily_average')} />
        </Card>
        <Card wide title={data.latestDay ? tx('ui.mobile.latest_day', { date: dateOn(data.latestDay.at, zone) }) : tx('ui.mobile.latest_day_none')}
          empty={data.latestDay === null} value={tx('ui.mobile.hour_by_hour')} detail={tx('ui.mobile.latest_day_detail')}>
          {data.latestDay && <LatestDay day={data.latestDay} zone={zone} />}
        </Card>
        <Card title={tx('ui.mobile.metric_steps')} empty={cards.steps === null}
          value={cards.steps && FORMAT.steps(counts(cards.steps))} detail={countDetail}>
          <DailyBars days={metric('steps')} when={when} format={FORMAT.steps} name={tx('ui.mobile.metric_steps')} />
        </Card>
        <Card title={tx('ui.mobile.metric_hrv')} empty={cards.hrv === null}
          value={cards.hrv && FORMAT.hrv(cards.hrv.average)} detail={meanDetail}>
          <DailyLine days={metric('hrv')} when={when} format={FORMAT.hrv} name={tx('ui.mobile.metric_hrv')} />
        </Card>
        <Card title={tx('ui.mobile.metric_blood_oxygen')} empty={cards.bloodOxygen === null}
          value={cards.bloodOxygen && FORMAT.bloodOxygen(cards.bloodOxygen.average)} detail={meanDetail}>
          <DailyLine days={metric('bloodOxygen')} when={when} format={FORMAT.bloodOxygen} name={tx('ui.mobile.metric_blood_oxygen')} />
        </Card>
        <Card title={tx('ui.mobile.metric_respiratory_rate')} empty={cards.respiratoryRate === null}
          value={cards.respiratoryRate && FORMAT.respiratoryRate(cards.respiratoryRate.average)} detail={meanDetail}>
          <DailyLine days={metric('respiratoryRate')} when={when} format={FORMAT.respiratoryRate} name={tx('ui.mobile.metric_respiratory_rate')} />
        </Card>
        <Card title={tx('ui.mobile.metric_blood_pressure')} empty={cards.bloodPressure === null}
          value={cards.bloodPressure && tx('ui.mobile.value_mmhg', { systolic: NUMBER.format(Math.round(cards.bloodPressure.systolic)), diastolic: NUMBER.format(Math.round(cards.bloodPressure.diastolic)) })}
          detail={tx('ui.mobile.estimated_by_device')}>
          <DailyLine days={metric('bloodPressure')} when={when} format={(value) => tx('ui.mobile.value_mm_hg', { value: NUMBER.format(Math.round(value)) })}
            name={tx('ui.mobile.systolic')} secondName={tx('ui.mobile.diastolic')} />
        </Card>
        <Card title={tx('ui.mobile.metric_calories')} empty={cards.calories === null}
          value={cards.calories && FORMAT.calories(counts(cards.calories))} detail={countDetail}>
          <DailyBars days={metric('calories')} when={when} format={FORMAT.calories} color={MID} name={tx('ui.mobile.metric_calories')} />
        </Card>
        <Card title={tx('ui.mobile.metric_distance')} empty={cards.distance === null}
          value={cards.distance && FORMAT.distance(counts(cards.distance))} detail={countDetail}>
          <DailyBars days={metric('distance')} when={when} format={FORMAT.distance} color={MID} name={tx('ui.mobile.metric_distance')} />
        </Card>
      </div>
      <p className="xo-mob-footnote"><ShieldCheck aria-hidden="true" />{tx('ui.mobile.health_note', { zone })}</p>
    </div>
  );
}

export function HealthUnavailable() {
  return <Empty title={tx('ui.mobile.health_restricted')} detail={tx('ui.mobile.health_restricted_detail')} />;
}
